import {
  isCartOrganizationStale,
  notifyCartChanged,
  notifyOtherTabsCartChanged,
} from '@lib/commerce/cartChanged';
import {
  areCartCountReadsHeld,
  CART_COUNT_STALE_TIME_MS,
  cartCountRetryDelay,
  holdCartCountReads,
  retryAfterMs,
  shouldRetryCartCount,
} from '@lib/commerce/cartCount';
import { configureCommerceReferenceQueries } from '@lib/commerce/commerceReferenceQueries';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  replaceEqualDeep,
  type Mutation,
  type Query,
} from '@tanstack/react-query';
import { getGetCartQueryKey, type Cart } from '@unity/commerce-bridge';
import axios from 'axios';

/** commerce-core's cart query, `getGetCartQueryKey(id)`, whatever the id. */
export const CART_QUERY_KEY = getGetCartQueryKey('').slice(0, -1);

/** commerce-core's cart query data. */
type CartQueryData = { cart?: Cart };

/** Whether a cart the client holds still has the line item. */
export const isLineInCart = (queryClient: QueryClient, lineItemId: string) =>
  queryClient
    .getQueriesData<CartQueryData>({ queryKey: CART_QUERY_KEY })
    .some(([, data]) =>
      data?.cart?.lineItems?.some(({ id }) => id === lineItemId)
    );

const isCartQuery = ({ queryKey }: Query<unknown, unknown, unknown>) =>
  CART_QUERY_KEY.every((part, index) => queryKey[index] === part);

/**
 * Bridge mutations that check or re-price the cart and leave its lines as
 * they are, so the header count and the PDP have nothing to reread.
 */
const LINE_PRESERVING_MUTATIONS = new Set([
  'calculateProration',
  'calculateTax',
  'changeCurrencyCart',
  'checkAddress',
  'validateCart',
  'validateTaxCode',
]);

const REMOVE_LINE_ITEMS = 'removeLineItemCart';

/** The part of commerce-bridge's `removeLineItemCart` variables read here. */
type RemoveLineItemsVariables = {
  data?: { body?: { lineItems?: { lineNumber: string }[] } };
};

const removedLineIds = (variables: unknown): string[] =>
  (variables as RemoveLineItemsVariables).data?.body?.lineItems?.map(
    ({ lineNumber }) => lineNumber
  ) ?? [];

const isRemoval = (mutation: Mutation<unknown, unknown, unknown>) =>
  mutation.options.mutationKey?.[0] === REMOVE_LINE_ITEMS;

const CHANGE_LINE_ITEM = 'changeLineItemCart';

const isNotFound = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404;

const isLineWrite = (mutation: Mutation<unknown, unknown, unknown>) =>
  isRemoval(mutation) || mutation.options.mutationKey?.[0] === CHANGE_LINE_ITEM;

/**
 * Answered, but `success: false`: commerce-core (0.25) writes no cart and
 * puts back a removed line, as for a request that failed.
 */
const isRefused = (data: unknown) =>
  (data as { success?: unknown } | undefined)?.success === false;

/** The part of commerce-bridge's `changeLineItemCart` variables read here. */
type ChangeLineItemVariables = {
  lineItemId?: string;
  data?: { body?: { lineItems?: { variantKey?: string }[] } };
};

/**
 * A seat or quantity change keeps the line and its variant, so neither the
 * header count (lines) nor the PDP (variant in cart) can change; a billing
 * cycle switch in the mini-cart changes the variant and still signals.
 */
const keepsVariant = (
  variables: unknown,
  variantKeyOf: (lineItemId: string) => string | undefined
): boolean => {
  const { lineItemId, data } = variables as ChangeLineItemVariables;
  const lineItems = data?.body?.lineItems ?? [];
  const variantKey = lineItemId ? variantKeyOf(lineItemId) : undefined;
  return (
    Boolean(variantKey) &&
    lineItems.length > 0 &&
    lineItems.every((lineItem) => lineItem.variantKey === variantKey)
  );
};

export type MiniCartMutationHandlers = {
  /** This tab asked to remove these line items. */
  onRemoveStarted: (lineIds: string[]) => void;
  /** That removal failed, so they are still in the cart. */
  onRemoveFailed: (lineIds: string[]) => void;
  /**
   * A removal of these lines that finds them gone (404) reports no error.
   * Asked once as each removal starts, so the answer can be released and an
   * earlier removal of the same lines settling first cannot take it.
   */
  takeMayFindLinesGone?: (lineIds: string[]) => boolean;
  /** The variant a line item holds in the cart the mini-cart has loaded. */
  variantKeyOf: (lineItemId: string) => string | undefined;
  /**
   * Any write settled, seat changes and failures too: the stored cart is
   * stale. Called before commerce-core writes the response into the cache.
   */
  onWriteSettled: () => void;
  /** A removal or seat change failed, and the lines are resynced. */
  onLineWriteFailed: () => void;
  onLineWriteSucceeded: () => void;
};

/**
 * The mini-cart island's query client (CPS-7696): each cart write signals the
 * PDP and other tabs once it settles, failed ones too; a seat change only
 * other tabs, once it lands. A failed write rereads the cart.
 */
export const createMiniCartQueryClient = ({
  onRemoveStarted,
  onRemoveFailed,
  takeMayFindLinesGone,
  variantKeyOf,
  onWriteSettled,
  onLineWriteFailed,
  onLineWriteSucceeded,
}: MiniCartMutationHandlers): QueryClient => {
  // Judged as the write starts, against the lines it was made from.
  const seatChanges = new WeakSet<Mutation<unknown, unknown, unknown>>();
  const mayFindLinesGone = new WeakSet<Mutation<unknown, unknown, unknown>>();
  // Set while a resync writes its cart, which structural sharing would
  // otherwise swap back for the equal one already cached.
  let isResyncing = false;
  // Bumped as each line write lands, so a failure resynced after a later
  // write landed reports nothing.
  let landedLineWrites = 0;
  /**
   * commerce-core (0.25) puts back the lines a failed removal hid, as they
   * were when it started, so not a write landed since; it puts back no failed
   * seat change, keeps the version a 412 refused, and resyncs its lines only
   * from a new cart object, which an unchanged reread is not. The line
   * objects stay: fresh ones make commerce-ui resend a seat count.
   */
  const resyncCart = async () => {
    // A read for an org the cart has left would move it back.
    if (!areCartCountReadsHeld() && !isCartOrganizationStale()) {
      await queryClient.refetchQueries({
        queryKey: CART_QUERY_KEY,
        type: 'active',
      });
    }
    isResyncing = true;
    try {
      for (const { queryKey, state } of queryClient
        .getQueryCache()
        .findAll({ queryKey: CART_QUERY_KEY })) {
        const { cart } = (state.data ?? {}) as CartQueryData;
        // A failed reread keeps the old cart; rewriting it would clear the
        // error, so a checked-out cart's 404 would show its lines again.
        if (!cart || state.status === 'error') continue;
        queryClient.setQueryData<CartQueryData>(
          queryKey,
          {
            ...(state.data as CartQueryData),
            cart: {
              ...cart,
              lineItems: [...cart.lineItems],
            },
          },
          // The read it came from, so the stored cart takes it as no newer.
          { updatedAt: state.dataUpdatedAt }
        );
      }
    } finally {
      isResyncing = false;
    }
  };
  const queryClient = new QueryClient({
    // The badge reads on every page, so nothing rereads on focus.
    defaultOptions: {
      queries: {
        staleTime: CART_COUNT_STALE_TIME_MS,
        refetchOnWindowFocus: false,
      },
    },
    // Only the cart read shares the hold: the PDP's cart reads pause on it.
    queryCache: new QueryCache({
      onError: (error, query) => {
        const retryAfter = retryAfterMs(error);
        if (retryAfter && isCartQuery(query)) {
          holdCartCountReads(Date.now() + retryAfter);
        }
      },
    }),
    mutationCache: new MutationCache({
      onMutate: (variables, mutation) => {
        if (isRemoval(mutation)) {
          const lineIds = removedLineIds(variables);
          onRemoveStarted(lineIds);
          if (takeMayFindLinesGone?.(lineIds)) mayFindLinesGone.add(mutation);
        }
        if (
          mutation.options.mutationKey?.[0] === CHANGE_LINE_ITEM &&
          keepsVariant(variables, variantKeyOf)
        ) {
          seatChanges.add(mutation);
        }
      },
      onSettled: (data, error, variables, _onMutateResult, mutation) => {
        const failed = Boolean(error) || isRefused(data);
        const wasGone = mayFindLinesGone.has(mutation) && isNotFound(error);
        if (failed && isRemoval(mutation)) {
          onRemoveFailed(removedLineIds(variables));
        }
        onWriteSettled();
        // After the stored cart is fenced, so the reread may be stored.
        if (failed) {
          const landedBefore = landedLineWrites;
          void resyncCart().then(() => {
            if (
              isLineWrite(mutation) &&
              landedLineWrites === landedBefore &&
              !wasGone
            ) {
              onLineWriteFailed();
            }
          });
        } else if (isLineWrite(mutation)) {
          landedLineWrites += 1;
          onLineWriteSucceeded();
        }
        const [name] = mutation.options.mutationKey ?? [];
        if (LINE_PRESERVING_MUTATIONS.has(String(name))) return;
        if (seatChanges.has(mutation)) {
          if (!failed) notifyOtherTabsCartChanged();
          return;
        }
        notifyCartChanged();
      },
    }),
  });
  // The cart read keeps to the cart count's rate-limit budget and backoff.
  queryClient.setQueryDefaults(CART_QUERY_KEY, {
    structuralSharing: (previous: unknown, next: unknown) =>
      isResyncing ? next : replaceEqualDeep(previous, next),
    // react-query counts failures from 0; the cart-count helpers from 1.
    retry: (failureCount, error) =>
      shouldRetryCartCount(error, failureCount + 1),
    // Called for a failure that is not retried too, so a 404 holds none.
    retryDelay: (failureCount, error) => {
      const delay = cartCountRetryDelay(failureCount + 1, error);
      if (shouldRetryCartCount(error, failureCount + 1)) {
        holdCartCountReads(Date.now() + delay);
      }
      return delay;
    },
  });
  configureCommerceReferenceQueries(queryClient);
  return queryClient;
};
